import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Search, ArrowRight, Star, ChevronLeft, ChevronRight } from 'lucide-react';
import { Property, Category } from '../types';
import { apiService } from '../services/api';
import { PropertyCard } from '../components/property/PropertyCard';
import { CategoryCard } from '../components/property/CategoryCard';
import { HeroSlider } from '../components/HeroSlider';
import { WhatsAppButton } from '../components/WhatsAppButton';

export const HomePage: React.FC = () => {
  const [featuredProperties, setFeaturedProperties] = useState<Property[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [currentTestimonialIndex, setCurrentTestimonialIndex] = useState(0);
  const [touchStartX, setTouchStartX] = useState(0);
  const [touchEndX, setTouchEndX] = useState(0);

  // Helper function to get initials from name
  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase();
  };

  // Handle touch gestures for mobile swiping
  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStartX(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEndX(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStartX || !touchEndX) return;
    
    const distance = touchStartX - touchEndX;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe) {
      setCurrentTestimonialIndex((prev) => (prev + 1) % testimonials.length);
    } else if (isRightSwipe) {
      setCurrentTestimonialIndex((prev) => 
        prev === 0 ? testimonials.length - 1 : prev - 1
      );
    }
  };

  const testimonials = [
    {
      name: 'Rajesh Patel',
      role: 'Farmhouse Owner, Pune',
      text: 'AARAN HOMES helped us find the perfect farmhouse for our family retreat. The location is serene, and their team guided us through every step of the purchase process. Our weekend getaways have never been better!',
      rating: 5,
    },
    {
      name: 'Priya Sharma',
      role: 'Agriculture Investor, Mumbai',
      text: 'Investing in agricultural plots through AARAN HOMES was the best financial decision we made. The returns have been consistent, and the land quality is exceptional. Highly recommend their expertise!',
      rating: 5,
    },
    {
      name: 'Amit Desai',
      role: 'Second Home Buyer, Nashik',
      text: 'After years of city life, we wanted a peaceful second home. AARAN HOMES found us a beautiful farmhouse with all modern amenities. It\'s our perfect escape from the hustle and bustle.',
      rating: 5,
    },
    {
      name: 'Sneha Kulkarni',
      role: 'Agricultural Plot Owner, Satara',
      text: 'The team at AARAN HOMES is incredibly professional and knowledgeable about agricultural investments. They helped us secure prime agricultural land that has appreciated significantly in value.',
      rating: 5,
    },
    {
      name: 'Manish Joshi',
      role: 'Farmhouse Developer, Lonavala',
      text: 'Working with AARAN HOMES for our farmhouse project was seamless. Their understanding of the market and legal expertise saved us time and money. Excellent service from start to finish.',
      rating: 5,
    },
    {
      name: 'Kavita Mehta',
      role: 'Investment Advisor, Kolhapur',
      text: 'I recommend AARAN HOMES to all my clients looking for agricultural investments. Their transparent approach and deep market knowledge make them the best choice for farmhouse and agricultural plot purchases.',
      rating: 5,
    },
    {
      name: 'Vikram Singh',
      role: 'Weekend Farmhouse Owner, Mahabaleshwar',
      text: 'Our family has been enjoying our farmhouse for 3 years now. AARAN HOMES found us the perfect location with mountain views and organic farming potential. Best investment we ever made!',
      rating: 5,
    },
    
   
   
  ];

  useEffect(() => {
    fetchData();
  }, []);

  // Auto-scroll testimonials every 1 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTestimonialIndex((prev) => (prev + 1) % testimonials.length);
    }, 1000);

    return () => clearInterval(interval);
  }, [testimonials.length]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [featuredResponse, categoriesResponse] = await Promise.all([
        apiService.getFeaturedProperties(4), // Get only 4 featured properties
        apiService.getCategories()
      ]);

      if (featuredResponse.success) {
        setFeaturedProperties(featuredResponse.data || []);
      }

      if (categoriesResponse.success) {
        setCategories(categoriesResponse.data || []);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const searchParams = new URLSearchParams();
    if (searchKeyword.trim()) {
      searchParams.set('keyword', searchKeyword.trim());
    }
    window.location.href = `/properties?${searchParams.toString()}`;
  };

  const stats = [
    { label: 'Farmhouse Projects', value: '500+', icon: '�' },
    { label: 'Happy Families', value: '850+', icon: '😊' },
    { label: 'Years Experience', value: '18+', icon: '⭐' },
    { label: 'Agricultural Plots', value: '1200+', icon: '�' },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Slider Section */}
      <HeroSlider autoPlay={true} autoPlayInterval={5000} />
      
      {/* Search Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
              Find Your Perfect Property
            </h2>
            <p className="text-xl text-gray-600 mb-8">
              Search through our extensive collection of properties to find your dream home
            </p>
            
            {/* Search Form */}
            <form onSubmit={handleSearch} className="bg-white rounded-2xl shadow-xl p-6 border border-gray-100">
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex-1">
                  <input
                    type="text"
                    value={searchKeyword}
                    onChange={(e) => setSearchKeyword(e.target.value)}
                    placeholder="Search for properties, locations, or features..."
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                  />
                </div>
                <button
                  type="submit"
                  className="bg-primary text-white px-8 py-3 rounded-lg font-semibold hover:bg-primary/90 transition-colors duration-200 flex items-center justify-center space-x-2"
                >
                  <Search className="w-5 h-5" />
                  <span>Search</span>
                </button>
              </div>
            </form>


          </div>
        </div>
      </section>

      {/* Categories Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Explore Property Categories
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Browse through our diverse range of property types to find exactly what you're looking for
            </p>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[...Array(4)].map((_, index) => (
                <div key={index} className="animate-pulse">
                  <div className="bg-white rounded-xl h-48"></div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {categories.map((category) => (
                <CategoryCard key={category.id} category={category} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Featured Properties Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="flex justify-between items-end mb-16">
            <div>
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
                Featured Properties
              </h2>
              <p className="text-xl text-gray-600 mb-2">
                Handpicked premium properties for discerning buyers
              </p>
              <p className="text-sm text-gray-500 md:hidden">
                Swipe right to see more →
              </p>
            </div>
            <Link
              to="/properties"
              className="hidden sm:flex items-center space-x-2 text-primary hover:text-primary/80 transition-colors duration-200"
            >
              <span className="font-medium">View All Properties</span>
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>

          {loading ? (
            <>
              {/* Mobile Loading */}
              <div className="md:hidden flex gap-4 overflow-x-auto pb-4 scrollbar-hide">
                {[...Array(4)].map((_, index) => (
                  <div key={index} className="animate-pulse flex-none w-72">
                    <div className="bg-gray-200 rounded-xl h-64 mb-4"></div>
                    <div className="space-y-2">
                      <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                      <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                    </div>
                  </div>
                ))}
              </div>
              {/* Desktop Loading */}
              <div className="hidden md:grid grid-cols-2 lg:grid-cols-4 gap-6">
                {[...Array(4)].map((_, index) => (
                  <div key={index} className="animate-pulse">
                    <div className="bg-gray-200 rounded-xl h-64 mb-4"></div>
                    <div className="space-y-2">
                      <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                      <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <>
              {/* Mobile Horizontal Scroll */}
              <div className="md:hidden flex gap-4 overflow-x-auto pb-4 scrollbar-hide">
                {featuredProperties.map((property) => (
                  <div key={property.id} className="flex-none w-72">
                    <PropertyCard property={property} />
                  </div>
                ))}
              </div>
              {/* Desktop Grid */}
              <div className="hidden md:grid grid-cols-2 lg:grid-cols-4 gap-6">
                {featuredProperties.map((property) => (
                  <PropertyCard key={property.id} property={property} />
                ))}
              </div>
            </>
          )}

          <div className="text-center mt-12 sm:hidden">
            <Link
              to="/properties"
              className="inline-flex items-center space-x-2 bg-primary text-white px-8 py-3 rounded-lg font-medium hover:bg-primary/90 transition-colors duration-200"
            >
              <span>View All Properties</span>
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              What Our Clients Say
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Don't just take our word for it - hear from our satisfied clients
            </p>
          </div>

          {/* Testimonials Slider */}
          <div 
            className="relative overflow-hidden"
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            <div 
              className="flex transition-transform duration-500 ease-in-out gap-8"
              style={{ transform: `translateX(-${currentTestimonialIndex * 100}%)` }}
            >
              {/* Create infinite loop by duplicating testimonials */}
              {[...testimonials, ...testimonials].map((testimonial, index) => (
                <div 
                  key={index} 
                  className="flex-none w-full md:w-1/2 lg:w-1/3 bg-white rounded-xl p-8 shadow-card hover:shadow-card-hover transition-shadow duration-300"
                >
                  <div className="flex items-center mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 text-secondary fill-current" />
                    ))}
                  </div>
                  <p className="text-gray-700 mb-6 leading-relaxed italic">
                    "{testimonial.text}"
                  </p>
                  <div className="flex items-center">
                    <div className="w-12 h-12 rounded-full bg-primary text-white flex items-center justify-center mr-4 font-bold text-lg">
                      {getInitials(testimonial.name)}
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900">{testimonial.name}</div>
                      <div className="text-sm text-gray-600">{testimonial.role}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Navigation Dots */}
            <div className="flex justify-center mt-8 space-x-2">
              {testimonials.slice(0, 5).map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentTestimonialIndex(index)}
                  className={`w-3 h-3 rounded-full transition-colors duration-200 ${
                    index === currentTestimonialIndex ? 'bg-primary' : 'bg-gray-300'
                  }`}
                />
              ))}
            </div>

            {/* Mobile: Show scroll indicator */}
            <div className="md:hidden text-center mt-4">
              <p className="text-sm text-gray-500">
                Swipe or wait for auto-scroll
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Projects Section */}
      <section className="py-1 bg-white">
        <div className="container mx-auto px-1">
          <div className="text-center mb-8">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Our Projects Timeline
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto mb-8">
              Stay tuned for updates on our running projects as we continue to bring our vision to life and create communities where residents can live, work, and thrive.
            </p>
          </div>
          
          <div className="max-w-6xl mx-auto">
            {/* Mobile Timeline */}
            <div className="md:hidden space-y-6 ml-6">
              {/* 2007-2009 */}
              <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 relative">
                <div className="absolute -left-9 top-6 w-6 h-6 bg-primary rounded-full border-4 border-white shadow-lg"></div>
                <h3 className="text-xl font-bold text-primary mb-2">2007-2009</h3>
                <p className="text-gray-700 font-medium">PEN PANVEL, 150 PLOTS</p>
              </div>
              
              {/* 2010-2012 */}
              <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 relative">
                <div className="absolute -left-9 top-6 w-6 h-6 bg-secondary rounded-full border-4 border-white shadow-lg"></div>
                <h3 className="text-xl font-bold text-primary mb-2">2010-2012</h3>
                <p className="text-gray-700 font-medium">LONAVALA, 40 FLATS AND 20 PLOTS</p>
              </div>
              
              {/* 2012-2014 */}
              <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 relative">
                <div className="absolute -left-9 top-6 w-6 h-6 bg-tertiary rounded-full border-4 border-white shadow-lg"></div>
                <h3 className="text-xl font-bold text-primary mb-2">2012-2014</h3>
                <p className="text-gray-700 font-medium">JAWHAR, 200 PLOTS</p>
              </div>
              
              {/* 2015-2019 */}
              <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 relative">
                <div className="absolute -left-9 top-6 w-6 h-6 bg-primary rounded-full border-4 border-white shadow-lg"></div>
                <h3 className="text-xl font-bold text-primary mb-2">2015-2019</h3>
                <p className="text-gray-700 font-medium">KAPOLI, 20 BUNGALOW INCLUDES VARIOUS PROJECTS ALL OVER THE MAHARASHTRA</p>
              </div>
              
              {/* 2021-2023 */}
              <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 relative">
                <div className="absolute -left-9 top-6 w-6 h-6 bg-secondary rounded-full border-4 border-white shadow-lg"></div>
                <h3 className="text-xl font-bold text-primary mb-2">2021-2023</h3>
                <p className="text-gray-700 font-medium">WADA, HOUSE OF ABHINANDHAN LODHA</p>
              </div>
              
              {/* 2022 */}
              <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 relative">
                <div className="absolute -left-9 top-6 w-6 h-6 bg-tertiary rounded-full border-4 border-white shadow-lg"></div>
                <h3 className="text-xl font-bold text-primary mb-2">2022</h3>
                <p className="text-gray-700 font-medium">KARNATAKA, 02 DEALS OF AGRICULTURE PLOTS OF 25 ACRE</p>
              </div>
            </div>

            {/* Desktop Timeline */}
            <div className="hidden md:block relative">
              {/* Timeline Line */}
              <div className="absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-gradient-to-b from-primary to-tertiary"></div>
              
              {/* Timeline Items */}
              <div className="space-y-12">
                {/* 2007-2009 */}
                <div className="flex items-center md:justify-start">
                  <div className="w-full md:w-5/12 bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
                    <div className="text-right">
                      <h3 className="text-xl font-bold text-primary mb-2">2007-2009</h3>
                      <p className="text-gray-700 font-medium">PEN PANVEL, 150 PLOTS</p>
                    </div>
                  </div>
                  <div className="hidden md:flex w-2/12 justify-center">
                    <div className="w-4 h-4 bg-primary rounded-full border-4 border-white shadow-lg"></div>
                  </div>
                  <div className="hidden md:block w-5/12"></div>
                </div>
                
                {/* 2010-2012 */}
                <div className="flex items-center md:justify-end">
                  <div className="hidden md:block w-5/12"></div>
                  <div className="hidden md:flex w-2/12 justify-center">
                    <div className="w-4 h-4 bg-secondary rounded-full border-4 border-white shadow-lg"></div>
                  </div>
                  <div className="w-full md:w-5/12 bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
                    <h3 className="text-xl font-bold text-primary mb-2">2010-2012</h3>
                    <p className="text-gray-700 font-medium">LONAVALA, 40 FLATS AND 20 PLOTS</p>
                  </div>
                </div>
                
                {/* 2012-2014 */}
                <div className="flex items-center md:justify-start">
                  <div className="w-full md:w-5/12 bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
                    <div className="text-right">
                      <h3 className="text-xl font-bold text-primary mb-2">2012-2014</h3>
                      <p className="text-gray-700 font-medium">JAWHAR, 200 PLOTS</p>
                    </div>
                  </div>
                  <div className="hidden md:flex w-2/12 justify-center">
                    <div className="w-4 h-4 bg-tertiary rounded-full border-4 border-white shadow-lg"></div>
                  </div>
                  <div className="hidden md:block w-5/12"></div>
                </div>
                
                {/* 2015-2019 */}
                <div className="flex items-center md:justify-end">
                  <div className="hidden md:block w-5/12"></div>
                  <div className="hidden md:flex w-2/12 justify-center">
                    <div className="w-4 h-4 bg-primary rounded-full border-4 border-white shadow-lg"></div>
                  </div>
                  <div className="w-full md:w-5/12 bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
                    <h3 className="text-xl font-bold text-primary mb-2">2015-2019</h3>
                    <p className="text-gray-700 font-medium">KAPOLI, 20 BUNGALOW INCLUDES VARIOUS PROJECTS ALL OVER THEMAHARASHTRA</p>
                  </div>
                </div>
                
                {/* 2021-2023 */}
                <div className="flex items-center md:justify-start">
                  <div className="w-full md:w-5/12 bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
                    <div className="text-right">
                      <h3 className="text-xl font-bold text-primary mb-2">2021-2023</h3>
                      <p className="text-gray-700 font-medium">WADA, HOUSE OF ABHINANDHAN LODHA</p>
                    </div>
                  </div>
                  <div className="hidden md:flex w-2/12 justify-center">
                    <div className="w-4 h-4 bg-secondary rounded-full border-4 border-white shadow-lg"></div>
                  </div>
                  <div className="hidden md:block w-5/12"></div>
                </div>
                
                {/* 2022 */}
                <div className="flex items-center md:justify-end">
                  <div className="hidden md:block w-5/12"></div>
                  <div className="hidden md:flex w-2/12 justify-center">
                    <div className="w-4 h-4 bg-tertiary rounded-full border-4 border-white shadow-lg"></div>
                  </div>
                  <div className="w-full md:w-5/12 bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
                    <h3 className="text-xl font-bold text-primary mb-2">2022</h3>
                    <p className="text-gray-700 font-medium">KARNATAKA, 02 DEALS OF AGRICULTURE PLOTS OF 25 ACRE</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 bg-gradient-primary text-white">
        <div className="container mx-auto px-1 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold mb-4">
            Ready to Start Your Farmhouse Journey?
          </h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto opacity-90">
            Discover the perfect blend of investment, enjoyment, and earning potential with our premium farmhouse projects. 
            Let nature's beauty become your gateway to financial prosperity.
          </p>
          <div className="flex justify-center">
            <Link
              to="/properties"
              className="bg-secondary text-primary px-8 py-4 rounded-lg font-semibold hover:bg-secondary/90 transition-colors duration-200"
            >
              Explore Properties
            </Link>
          </div>
        </div>
      </section>

      {/* WhatsApp Button */}
      <WhatsAppButton />
    </div>
  );
};